import React, {useEffect} from 'react';
import {useNavigate, useSearchParams} from 'react-router';
import {Button} from '@interstate/components/Button';
import {Alert} from '@interstate/components/Alert';
import {useAuth} from '../context/AuthContext';
import {FullScreenLoadingState} from '../components/States';

/** Friendly message for an `?error=` code the SSO callback may append on a failed sign-in. */
const SSO_ERRORS: Record<string, string> = {
  state: 'Your sign-in session expired or could not be verified. Please try again.',
  auth: 'We could not verify your Microsoft sign-in. Please try again.'
};
function ssoErrorMessage(code: string | null): string {
  if (!code) return '';
  return SSO_ERRORS[code] ?? 'Sign-in was cancelled or could not be completed. Please try again.';
}

export default function Login(): React.ReactElement {
  const {loginWithMicrosoft, isAuthenticated, authReady} = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const error = ssoErrorMessage(searchParams.get('error'));

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', {replace: true});
  }, [isAuthenticated, navigate]);

  // Wait for the initial session probe before showing the sign-in form — otherwise a
  // cookie-backed session (e.g. the SSO redirect landing here, or a refresh) flashes the
  // "Sign in with Microsoft" button before this effect fires and bounces to /dashboard.
  if (!authReady) return <FullScreenLoadingState label="Signing you in…" />;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2.5 mb-2">
          <div className="w-px h-5 bg-blue-700" />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Cox Automotive</span>
        </div>
        <p className="text-xs text-gray-400 tracking-wide">Enterprise Technology &amp; Engineering</p>
      </div>

      <div className="w-full max-w-md bg-white rounded-xl border border-gray-100 shadow-md overflow-hidden">
        <div className="h-1 bg-blue-700" />
        <div className="px-8 py-8">
          <h1 className="text-xl font-bold text-gray-900 mb-1">ETE AIT Leader Dashboard</h1>
          <p className="text-sm text-gray-500 mb-6">This dashboard is restricted. Sign in to continue.</p>

          {error && (
            <Alert type="error" sx={{marginBottom: '1rem'}}>
              {error}
            </Alert>
          )}

          <Button buttonStyle="primary" type="button" block size="medium" onClick={loginWithMicrosoft}>
            Sign in with Microsoft
          </Button>
        </div>
      </div>

      <p className="mt-5 text-xs text-gray-400">Need access? Contact your leader's chief of staff.</p>
    </div>
  );
}
